import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  catchError,
  filter,
  map,
  merge,
  shareReplay,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { UserService } from '@ipg/ngx-frontend-toolset';
import { Device } from '../../models/devices.model';
import { User } from '../../models/user.model';
import { UnitResponseModel } from '../../views/units/units.model';
import { BaseDialog } from '../dialog/base-dialog';
import { ConfirmHandler } from '../dialog/dialog.model';
import { UndoUnitTransferSnackbarComponent } from '../undo-unit-transfer-snackbar/undo-unit-transfer-snackbar.component';

@Component({
  selector: 'app-device-transfer-dialog',
  imports: [
    MatButton,
    MatFormFieldModule,
    MatSelectModule,
    MatDialogModule,
    MatProgressSpinner,
  ],
  templateUrl: './device-transfer-dialog.component.html',
  styleUrl: './device-transfer-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceTransferDialogComponent extends BaseDialog<{
  device: Device;
  units: UnitResponseModel[];
  onConfirm: ConfirmHandler;
  resetDataHandler: (() => void) | null;
}> {
  protected readonly selectedUnit: UnitResponseModel | null = null;

  private readonly _dialogRef =
    inject<MatDialogRef<DeviceTransferDialogComponent>>(MatDialogRef);
  private readonly user = inject<UserService<User>>(UserService);
  private readonly snackbar = inject(MatSnackBar);

  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  protected readonly control = new FormControl(null, {
    validators: [Validators.required],
  });
  protected readonly current_unit_id = this.user.data()?.unitId;

  protected isUnitSelectable(unit: UnitResponseModel): boolean {
    return this.data.device.unitId === unit._id;
  }

  override handleConfirm(): void {
    if (!this.selectedUnit) {
      this._dialogRef.close(true);
      return;
    }
    const previousUnitId = this.data.device.unitId;

    this.submitted.set(true);
    this.control.disable();

    const delay = 1000;
    const callback$ = this.data.onConfirm(this.selectedUnit);
    const source$ = callback$.pipe(
      shareReplay(1),
      catchError((error) => {
        this.submitted.set(false);
        this.control.enable();
        this.triggerSnackbar(false, previousUnitId);

        return throwError(() => error);
      })
    );

    const loading$ = merge(
      timer(delay).pipe(
        map(() => true),
        takeUntil(source$)
      ),
      source$.pipe(map(() => false))
    );

    loading$
      .pipe(
        tap((state) => this.loading.set(state)),
        filter((state) => !state),
        tap(() => {
          this._dialogRef.close(true);
          this.triggerSnackbar(true, previousUnitId);
        }),
        take(1)
      )
      .subscribe({
        error: () => {
          console.debug(`Caught an exception.`);
        },
      });
  }
  private triggerSnackbar(isSuccess: boolean, previousUnitId: string): void {
    this.snackbar.openFromComponent(UndoUnitTransferSnackbarComponent, {
      data: {
        unit_id: previousUnitId,
        device_id: this.data.device._id,
        resetDataHandler: this.data.resetDataHandler,
        isSuccess: isSuccess,
      },
    });
  }
}
